/**
 * The header's center slot — the smallest generic mechanism for a route to
 * put content in the middle of the sticky app header (`AppShell`'s
 * `headerCenter` prop), without the shell knowing what any particular route
 * wants there. Nothing in the template uses it yet; it exists so a project
 * does not have to fork `AppShell` the first time a screen needs a control
 * between the title and Search (DESIGN.md "Header center slot").
 *
 * `__root.tsx` renders `<HeaderSlotProvider>` around the routed content and
 * passes its state into `<AppShell headerCenter>`. A screen calls
 * `useHeaderCenterSlot(node)` and the node appears in the header for as long
 * as that screen is mounted — unmounting clears it automatically.
 */
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'

type SetHeaderCenter = (node: ReactNode) => void

const HeaderSlotContext = createContext<SetHeaderCenter | null>(null)

export function HeaderSlotProvider({
  children,
}: {
  children: (headerCenter: ReactNode) => ReactNode
}) {
  const [node, setNode] = useState<ReactNode>(null)
  return <HeaderSlotContext.Provider value={setNode}>{children(node)}</HeaderSlotContext.Provider>
}

/** Puts `node` in the header's center slot while the calling component is mounted. */
export function useHeaderCenterSlot(node: ReactNode): void {
  const setHeaderCenter = useContext(HeaderSlotContext)
  useEffect(() => {
    setHeaderCenter?.(node)
    return () => setHeaderCenter?.(null)
  }, [setHeaderCenter, node])
}
